import {
  Component,
  computed,
  inject,
  input,
  output,
  signal,
  viewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { COMMON_IMPORTS } from '../../../../Shared/common-imports';
import { AvTableLoadingBarComponent } from '../../../../Shared/Components/loading-bar/table-loading-bar.component';
import { AutopilotDevicesFacade } from '../../../../../Facades/Intune/autopilot-devices.facade';
import { AutopilotDevice } from '../../../../../Models/Intune/autopilot-device.model';
import { AuthService } from '../../../../Shared/Services/auth.service';
import { PERMISSIONS } from '../../../../../Enums/permission.enum';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'av-autopilot-devices-table',
  standalone: true,
  imports: [COMMON_IMPORTS, AvTableLoadingBarComponent],
  templateUrl: './autopilot-devices-table.component.html',
  styleUrls: [
    './autopilot-devices-table.component.scss',
    '../../../../../../assets/theme/cutom/_av-table.scss',
  ],
})
export class AutopilotDevicesTableComponent implements AfterViewInit, OnDestroy {
  readonly facade = input.required<AutopilotDevicesFacade>();
  private readonly _authService = inject(AuthService);

  readonly currentUser = toSignal(this._authService.currentUser$, { initialValue: null });
  permissions = PERMISSIONS;

  readonly delete = output<string>();

  readonly isBottomReached = signal(false);

  readonly dataSource = computed(
    () => new MatTableDataSource<AutopilotDevice>(this.facade().devices()),
  );
  readonly displayedColumns: string[] = [
    'index',
    'serialNumber',
    'manufacturer',
    'model',
    'groupTag',
    'enrollmentState',
    'lastContactedDateTime',
  ];

  ngOnInit() {
    this.initActionColumn();
  }
  
  private initActionColumn(): void {
    if (this.currentUser()?.hasPermission(this.permissions.AUTOPILOT_DEVICES_DELETE)) {
      this.displayedColumns.push('actions');
    }
  }

  readonly bottomDetector = viewChild<ElementRef>('bottomDetector');
  readonly tableWrapper = viewChild<ElementRef>('tableWrapper');

  private _observer: IntersectionObserver | null = null;

  ngAfterViewInit() {
    this.setupScrollObserver();
  }

  ngOnDestroy() {
    this._observer?.disconnect();
  }

  deleteDevice(id: string) {
    this.delete.emit(id);
  }

  private setupScrollObserver() {
    const el = this.bottomDetector()?.nativeElement;
    if (!el) return;
    this._observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => this.isBottomReached.set(e.isIntersecting)),
      { root: this.tableWrapper()?.nativeElement ?? null, threshold: 0.1 },
    );
    this._observer.observe(el);
  }
}
